import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { formatDuration, formatApiError } from '../../lib/utils';
import { 
    ArrowLeft, 
    Upload, 
    Music, 
    Plus, 
    Trash2, 
    Play, 
    Pause,
    Globe,
    FileAudio,
    Save,
    Loader2,
    Image,
    Edit2,
    X,
    MapPin,
    FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

const API = process.env.REACT_APP_BACKEND_URL;

// Gold map marker icon
const goldIcon = new L.Icon({
    iconUrl: 'https://cdn.jsdelivr.net/gh/pointhi/leaflet-color-markers@master/img/marker-icon-gold.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

function MiniMapPicker({ lat, lng, onSelect }) {
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) return null;

    function ClickHandler() {
        useMapEvents({
            click(e) {
                onSelect(e.latlng.lat, e.latlng.lng);
            }
        });
        return null;
    }

    return (
        <div className="rounded-lg overflow-hidden border border-white/10 h-[180px] mt-2" data-testid="mini-map-picker">
            <MapContainer center={[lat, lng]} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[lat, lng]} icon={goldIcon} />
                <ClickHandler />
            </MapContainer>
        </div>
    );
}

const LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'it', name: 'Italian' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'ar', name: 'Arabic' }
];

const AUDIO_TYPES = [
    { value: 'intro', label: 'Introduction' },
    { value: 'main', label: 'Main Content' },
    { value: 'room', label: 'Room/Section' },
    { value: 'masterpiece', label: 'Masterpiece/POI' },
    { value: 'history', label: 'Historical Context' },
    { value: 'tips', label: 'Visitor Tips' },
    { value: 'outro', label: 'Goodbye/Outro' }
];

export default function AdminAttractionAudio() {
    const { attractionId } = useParams();
    const [attraction, setAttraction] = useState(null);
    const [audioContent, setAudioContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [playingId, setPlayingId] = useState(null);
    const [editingAudio, setEditingAudio] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(null);
    const [transcribing, setTranscribing] = useState(null);
    const [bulkTranscribing, setBulkTranscribing] = useState(false);
    const [bulkProgress, setBulkProgress] = useState(null);
    const audioRef = useRef(null);
    const imageInputRef = useRef(null);
    
    const [newAudio, setNewAudio] = useState({
        title: '',
        language: 'en',
        audio_type: 'main',
        transcript: '',
        order: 0,
        image_url: '',
        latitude: '',
        longitude: '',
        trigger_radius: ''
    });
    const [selectedFile, setSelectedFile] = useState(null);

    useEffect(() => {
        fetchData();
    }, [attractionId]);

    const fetchData = async () => {
        try {
            const [attrRes, audioRes] = await Promise.all([
                axios.get(`${API}/api/admin/attractions/${attractionId}`, { withCredentials: true }),
                axios.get(`${API}/api/admin/audio?attraction_id=${attractionId}`, { withCredentials: true })
            ]);
            setAttraction(attrRes.data);
            setAudioContent(audioRes.data);
        } catch (error) {
            toast.error('Failed to fetch attraction data');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAudio = async (e) => {
        e.preventDefault();
        if (!selectedFile) {
            toast.error('Please select an audio file');
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        try {
            // First create audio content entry
            const payload = { attraction_id: attractionId, ...newAudio };
            if (payload.latitude !== '' && payload.longitude !== '') {
                payload.latitude = parseFloat(payload.latitude);
                payload.longitude = parseFloat(payload.longitude);
            } else {
                delete payload.latitude;
                delete payload.longitude;
            }
            if (payload.trigger_radius !== '' && payload.trigger_radius != null) {
                payload.trigger_radius = parseInt(payload.trigger_radius);
            } else {
                delete payload.trigger_radius;
            }
            const createRes = await axios.post(`${API}/api/admin/audio`, payload, { withCredentials: true });

            const audioId = createRes.data.id;
            setUploadProgress(30);

            // Then upload the audio file
            const formData = new FormData();
            formData.append('file', selectedFile);

            await axios.post(`${API}/api/admin/audio/${audioId}/upload`, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percent = Math.round((progressEvent.loaded * 70) / progressEvent.total) + 30;
                    setUploadProgress(percent);
                }
            });

            toast.success('Audio uploaded successfully');
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleUpdateAudio = async (e) => {
        e.preventDefault();
        if (!editingAudio) return;

        try {
            const payload = {
                title: editingAudio.title,
                language: editingAudio.language,
                audio_type: editingAudio.audio_type,
                transcript: editingAudio.transcript,
                order: editingAudio.order,
                image_url: editingAudio.image_url
            };
            if (editingAudio.latitude !== '' && editingAudio.latitude != null && editingAudio.longitude !== '' && editingAudio.longitude != null) {
                payload.latitude = parseFloat(editingAudio.latitude);
                payload.longitude = parseFloat(editingAudio.longitude);
            } else {
                payload.latitude = null;
                payload.longitude = null;
            }
            if (editingAudio.trigger_radius !== '' && editingAudio.trigger_radius != null) {
                payload.trigger_radius = parseInt(editingAudio.trigger_radius);
            } else {
                payload.trigger_radius = null;
            }
            await axios.put(`${API}/api/admin/audio/${editingAudio.id}`, payload, { withCredentials: true });

            toast.success('Audio track updated');
            setEditDialogOpen(false);
            setEditingAudio(null);
            fetchData();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        }
    };

    const handleUploadImage = async (audioId, file) => {
        setUploadingImage(audioId);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(`${API}/api/admin/audio/${audioId}/image`, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success('Image uploaded');
            fetchData();
            
            // Update editing audio if in edit mode
            if (editingAudio && editingAudio.id === audioId) {
                setEditingAudio({ ...editingAudio, image_url: response.data.url });
            }
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            setUploadingImage(null);
        }
    };

    const handleDelete = async (audioId) => {
        if (!window.confirm('Are you sure you want to delete this audio track?')) return;
        try {
            await axios.delete(`${API}/api/admin/audio/${audioId}`, { withCredentials: true });
            toast.success('Audio deleted');
            fetchData();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm(`Delete ALL ${audioContent.length} audio tracks for this attraction? This cannot be undone.`)) return;
        if (!window.confirm('Are you absolutely sure? This will remove all audio content.')) return;
        try {
            const res = await axios.delete(`${API}/api/admin/audio/attraction/${attractionId}`, { withCredentials: true });
            toast.success(`Deleted ${res.data.deleted_count} audio tracks`);
            fetchData();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        }
    };

    const togglePlay = (audioId, filePath) => {
        if (playingId === audioId) {
            audioRef.current?.pause();
            setPlayingId(null);
        } else {
            if (audioRef.current) {
                audioRef.current.src = `${API}/api/files/${filePath}`;
                audioRef.current.play();
                setPlayingId(audioId);
            }
        }
    };

    const generateTranscript = async (audioId) => {
        setTranscribing(audioId);
        try {
            const res = await axios.post(`${API}/api/admin/audio/${audioId}/transcribe`, {}, { withCredentials: true });
            toast.success('Transcript generated successfully');
            fetchData();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail) || 'Transcription failed');
        } finally {
            setTranscribing(null);
        }
    };

    const untranscribedCount = audioContent.filter(a => a.file_path && !a.transcript).length;

    const bulkTranscribeAll = async () => {
        setBulkTranscribing(true);
        setBulkProgress({ completed: 0, total: untranscribedCount, failed: 0 });
        try {
            const res = await axios.post(`${API}/api/admin/audio/bulk-transcribe/${attractionId}`, {}, { withCredentials: true });
            const { job_id, total } = res.data;
            
            if (!job_id) {
                toast.info(res.data.message);
                setBulkTranscribing(false);
                setBulkProgress(null);
                return;
            }

            // Poll for status
            const poll = setInterval(async () => {
                try {
                    const statusRes = await axios.get(`${API}/api/admin/audio/bulk-transcribe-status/${job_id}`, { withCredentials: true });
                    const { completed, failed, status } = statusRes.data;
                    setBulkProgress({ completed, total, failed });
                    
                    if (status === 'done') {
                        clearInterval(poll);
                        setBulkTranscribing(false);
                        setBulkProgress(null);
                        fetchData();
                        if (failed > 0) {
                            toast.warning(`Transcribed ${completed} tracks, ${failed} failed`);
                        } else {
                            toast.success(`Successfully transcribed ${completed} tracks`);
                        }
                    }
                } catch {
                    clearInterval(poll);
                    setBulkTranscribing(false);
                    setBulkProgress(null);
                }
            }, 3000);
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail) || 'Bulk transcription failed');
            setBulkTranscribing(false);
            setBulkProgress(null);
        }
    };

    const openEditDialog = (audio) => {
        setEditingAudio({ ...audio });
        setEditDialogOpen(true);
    };

    const resetForm = () => {
        setNewAudio({
            title: '',
            language: 'en',
            audio_type: 'main',
            transcript: '',
            order: audioContent.length,
            image_url: '',
            latitude: '',
            longitude: '',
            trigger_radius: ''
        });
        setSelectedFile(null);
    };

    if (loading) {
        return (
            <DashboardLayout type="admin">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout type="admin">
            <div className="space-y-8" data-testid="admin-attraction-audio">
                {/* Hidden audio element */}
                <audio ref={audioRef} onEnded={() => setPlayingId(null)} />

                {/* Header */}
                <div className="flex items-center gap-4">
                    <Link to="/admin/attractions">
                        <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-white">
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                    </Link>
                    <div className="flex-1">
                        <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">{attraction?.title}</h1>
                        <p className="text-muted-foreground mt-1">Manage audio tracks & points of interest</p>
                    </div>
                    {untranscribedCount > 0 && (
                        <Button
                            variant="outline"
                            onClick={bulkTranscribeAll}
                            disabled={bulkTranscribing}
                            className="border-white/10 text-muted-foreground hover:text-white gap-2"
                            data-testid="bulk-transcribe-btn"
                        >
                            {bulkTranscribing ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {bulkProgress ? `${bulkProgress.completed}/${bulkProgress.total}` : 'Starting...'}
                                </>
                            ) : (
                                <>
                                    <FileText className="w-4 h-4" />
                                    Transcribe All ({untranscribedCount})
                                </>
                            )}
                        </Button>
                    )}
                    {audioContent.length > 0 && (
                        <Button
                            variant="outline"
                            onClick={handleDeleteAll}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 gap-2"
                            data-testid="delete-all-tracks-btn"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete All ({audioContent.length})
                        </Button>
                    )}
                    <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                        <DialogTrigger asChild>
                            <Button className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="add-audio-btn">
                                <Plus className="w-4 h-4" /> Add Audio Track
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#121620] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="text-white font-outfit">Add New Audio Track</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleCreateAudio} className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Title / Point of Interest Name</Label>
                                        <Input
                                            value={newAudio.title}
                                            onChange={(e) => setNewAudio({ ...newAudio, title: e.target.value })}
                                            placeholder="e.g., The Gladiator Arena"
                                            required
                                            className="bg-[#0A0D14] border-white/10"
                                            data-testid="audio-title-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Language</Label>
                                        <Select value={newAudio.language} onValueChange={(v) => setNewAudio({ ...newAudio, language: v })}>
                                            <SelectTrigger className="bg-[#0A0D14] border-white/10">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#121620] border-white/10">
                                                {LANGUAGES.map(lang => (
                                                    <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Type</Label>
                                        <Select value={newAudio.audio_type} onValueChange={(v) => setNewAudio({ ...newAudio, audio_type: v })}>
                                            <SelectTrigger className="bg-[#0A0D14] border-white/10">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#121620] border-white/10">
                                                {AUDIO_TYPES.map(type => (
                                                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Order</Label>
                                        <Input
                                            type="number"
                                            value={newAudio.order}
                                            onChange={(e) => setNewAudio({ ...newAudio, order: parseInt(e.target.value) || 0 })}
                                            min={0}
                                            className="bg-[#0A0D14] border-white/10"
                                        />
                                    </div>
                                </div>
                                {/* Location Image - in Add form */}
                                <div className="space-y-2">
                                    <Label>Location Image (Point of Interest Photo)</Label>
                                    <div className="flex gap-4">
                                        <div className="flex-1">
                                            <Input
                                                value={newAudio.image_url}
                                                onChange={(e) => setNewAudio({ ...newAudio, image_url: e.target.value })}
                                                placeholder="Paste image URL or upload after creating..."
                                                className="bg-[#0A0D14] border-white/10"
                                                data-testid="add-image-url-input"
                                            />
                                        </div>
                                        {newAudio.image_url && (
                                            <div className="relative w-16 h-16 rounded-md overflow-hidden border border-white/10 shrink-0">
                                                <img
                                                    src={newAudio.image_url}
                                                    alt="Preview"
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground">Paste an image URL, or upload a photo after creating the track</p>
                                </div>

                                {/* GPS Location */}
                                <div className="space-y-3 p-4 rounded-xl bg-[#0A0D14] border border-white/10">
                                    <div className="flex items-center gap-2 mb-1">
                                        <MapPin className="w-4 h-4 text-gold" />
                                        <Label className="text-sm font-medium">GPS Location (Optional)</Label>
                                    </div>
                                    <p className="text-xs text-muted-foreground -mt-1">Set coordinates to enable proximity-triggered audio for travelers</p>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Latitude</Label>
                                            <Input
                                                type="number"
                                                step="any"
                                                value={newAudio.latitude}
                                                onChange={(e) => setNewAudio({ ...newAudio, latitude: e.target.value })}
                                                placeholder="41.8902"
                                                className="bg-[#121620] border-white/10 text-sm"
                                                data-testid="add-latitude-input"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Longitude</Label>
                                            <Input
                                                type="number"
                                                step="any"
                                                value={newAudio.longitude}
                                                onChange={(e) => setNewAudio({ ...newAudio, longitude: e.target.value })}
                                                placeholder="12.4922"
                                                className="bg-[#121620] border-white/10 text-sm"
                                                data-testid="add-longitude-input"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Radius (m)</Label>
                                            <Input
                                                type="number"
                                                min={10}
                                                max={500}
                                                value={newAudio.trigger_radius}
                                                onChange={(e) => setNewAudio({ ...newAudio, trigger_radius: e.target.value })}
                                                placeholder="50"
                                                className="bg-[#121620] border-white/10 text-sm"
                                                data-testid="add-radius-input"
                                            />
                                        </div>
                                    </div>
                                    {newAudio.latitude && newAudio.longitude && (
                                        <MiniMapPicker
                                            lat={parseFloat(newAudio.latitude)}
                                            lng={parseFloat(newAudio.longitude)}
                                            onSelect={(lat, lng) => setNewAudio({ ...newAudio, latitude: lat.toFixed(6), longitude: lng.toFixed(6) })}
                                        />
                                    )}
                                    {(!newAudio.latitude || !newAudio.longitude) && (
                                        <button
                                            type="button"
                                            onClick={() => setNewAudio({ ...newAudio, latitude: '41.8902', longitude: '12.4922' })}
                                            className="text-xs text-gold hover:text-gold/80 underline"
                                        >
                                            Set sample Rome coordinates
                                        </button>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label>Audio File (MP3)</Label>
                                    <div className="border-2 border-dashed border-white/10 rounded-md p-6 text-center hover:border-gold/50 transition-colors">
                                        <input
                                            type="file"
                                            accept="audio/mp3,audio/mpeg,audio/wav"
                                            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                                            className="hidden"
                                            id="audio-file"
                                            data-testid="audio-file-input"
                                        />
                                        <label htmlFor="audio-file" className="cursor-pointer">
                                            {selectedFile ? (
                                                <div className="flex items-center justify-center gap-3">
                                                    <FileAudio className="w-8 h-8 text-gold" />
                                                    <div className="text-left">
                                                        <p className="text-white font-medium">{selectedFile.name}</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                                                    <p className="text-muted-foreground">Click to upload audio file</p>
                                                    <p className="text-xs text-muted-foreground mt-1">MP3, WAV up to 100MB</p>
                                                </div>
                                            )}
                                        </label>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Transcript (Optional)</Label>
                                    <Textarea
                                        value={newAudio.transcript}
                                        onChange={(e) => setNewAudio({ ...newAudio, transcript: e.target.value })}
                                        placeholder="Full transcript of the audio..."
                                        rows={3}
                                        className="bg-[#0A0D14] border-white/10"
                                    />
                                </div>
                                {uploading && (
                                    <div className="space-y-2">
                                        <Progress value={uploadProgress} className="h-2" />
                                        <p className="text-sm text-muted-foreground text-center">Uploading... {uploadProgress}%</p>
                                    </div>
                                )}
                                <div className="flex justify-end gap-3 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10" disabled={uploading}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" disabled={uploading} data-testid="upload-audio-btn">
                                        {uploading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-4 h-4" />
                                                Upload Audio
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Edit Dialog */}
                <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setEditingAudio(null); }}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit">Edit Audio Track</DialogTitle>
                        </DialogHeader>
                        {editingAudio && (
                            <form onSubmit={handleUpdateAudio} className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Title / Point of Interest</Label>
                                        <Input
                                            value={editingAudio.title}
                                            onChange={(e) => setEditingAudio({ ...editingAudio, title: e.target.value })}
                                            required
                                            className="bg-[#0A0D14] border-white/10"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Language</Label>
                                        <Select value={editingAudio.language} onValueChange={(v) => setEditingAudio({ ...editingAudio, language: v })}>
                                            <SelectTrigger className="bg-[#0A0D14] border-white/10">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#121620] border-white/10">
                                                {LANGUAGES.map(lang => (
                                                    <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Type</Label>
                                        <Select value={editingAudio.audio_type} onValueChange={(v) => setEditingAudio({ ...editingAudio, audio_type: v })}>
                                            <SelectTrigger className="bg-[#0A0D14] border-white/10">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#121620] border-white/10">
                                                {AUDIO_TYPES.map(type => (
                                                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Order</Label>
                                        <Input
                                            type="number"
                                            value={editingAudio.order}
                                            onChange={(e) => setEditingAudio({ ...editingAudio, order: parseInt(e.target.value) || 0 })}
                                            min={0}
                                            className="bg-[#0A0D14] border-white/10"
                                        />
                                    </div>
                                </div>
                                
                                {/* Location Image */}
                                <div className="space-y-2">
                                    <Label>Location Image (Point of Interest Photo)</Label>
                                    <div className="space-y-3">
                                        <div className="flex gap-4 items-start">
                                            <div className="flex-1 space-y-2">
                                                <Input
                                                    value={editingAudio.image_url || ''}
                                                    onChange={(e) => setEditingAudio({ ...editingAudio, image_url: e.target.value })}
                                                    placeholder="Paste image URL here..."
                                                    className="bg-[#0A0D14] border-white/10"
                                                    data-testid="edit-image-url-input"
                                                />
                                                <div className="border-2 border-dashed border-white/10 rounded-md p-3 text-center hover:border-gold/50 transition-colors">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) handleUploadImage(editingAudio.id, file);
                                                        }}
                                                        className="hidden"
                                                        id={`edit-image-${editingAudio.id}`}
                                                    />
                                                    <label htmlFor={`edit-image-${editingAudio.id}`} className="cursor-pointer">
                                                        {uploadingImage === editingAudio.id ? (
                                                            <div className="flex items-center justify-center gap-2">
                                                                <Loader2 className="w-5 h-5 animate-spin text-gold" />
                                                                <span className="text-muted-foreground text-sm">Uploading...</span>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center justify-center gap-2">
                                                                <Image className="w-4 h-4 text-muted-foreground" />
                                                                <span className="text-muted-foreground text-sm">Or upload from device</span>
                                                            </div>
                                                        )}
                                                    </label>
                                                </div>
                                            </div>
                                            {editingAudio.image_url && (
                                                <div className="relative w-24 h-24 rounded-md overflow-hidden border border-white/10 shrink-0">
                                                    <img 
                                                        src={editingAudio.image_url.startsWith('http') ? editingAudio.image_url : `${API}${editingAudio.image_url}`} 
                                                        alt="Location" 
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditingAudio({ ...editingAudio, image_url: '' })}
                                                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center hover:bg-red-500/80 transition-colors"
                                                    >
                                                        <X className="w-3 h-3 text-white" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground">Paste a URL or upload — travelers see this photo for the point of interest</p>
                                </div>

                                {/* GPS Location */}
                                <div className="space-y-3 p-4 rounded-xl bg-[#0A0D14] border border-white/10">
                                    <div className="flex items-center gap-2 mb-1">
                                        <MapPin className="w-4 h-4 text-gold" />
                                        <Label className="text-sm font-medium">GPS Location</Label>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Latitude</Label>
                                            <Input
                                                type="number"
                                                step="any"
                                                value={editingAudio.latitude ?? ''}
                                                onChange={(e) => setEditingAudio({ ...editingAudio, latitude: e.target.value })}
                                                placeholder="41.8902"
                                                className="bg-[#121620] border-white/10 text-sm"
                                                data-testid="edit-latitude-input"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Longitude</Label>
                                            <Input
                                                type="number"
                                                step="any"
                                                value={editingAudio.longitude ?? ''}
                                                onChange={(e) => setEditingAudio({ ...editingAudio, longitude: e.target.value })}
                                                placeholder="12.4922"
                                                className="bg-[#121620] border-white/10 text-sm"
                                                data-testid="edit-longitude-input"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Radius (m)</Label>
                                            <Input
                                                type="number"
                                                min={10}
                                                max={500}
                                                value={editingAudio.trigger_radius ?? ''}
                                                onChange={(e) => setEditingAudio({ ...editingAudio, trigger_radius: e.target.value })}
                                                placeholder="50"
                                                className="bg-[#121620] border-white/10 text-sm"
                                                data-testid="edit-radius-input"
                                            />
                                        </div>
                                    </div>
                                    {editingAudio.latitude && editingAudio.longitude && (
                                        <MiniMapPicker
                                            lat={parseFloat(editingAudio.latitude)}
                                            lng={parseFloat(editingAudio.longitude)}
                                            onSelect={(lat, lng) => setEditingAudio({ ...editingAudio, latitude: lat.toFixed(6), longitude: lng.toFixed(6) })}
                                        />
                                    )}
                                    {(!editingAudio.latitude || !editingAudio.longitude) && (
                                        <button
                                            type="button"
                                            onClick={() => setEditingAudio({ ...editingAudio, latitude: '41.8902', longitude: '12.4922' })}
                                            className="text-xs text-gold hover:text-gold/80 underline"
                                        >
                                            Set sample Rome coordinates
                                        </button>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label>Transcript</Label>
                                    <Textarea
                                        value={editingAudio.transcript || ''}
                                        onChange={(e) => setEditingAudio({ ...editingAudio, transcript: e.target.value })}
                                        placeholder="Full transcript of the audio..."
                                        rows={4}
                                        className="bg-[#0A0D14] border-white/10"
                                    />
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="border-white/10">
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2">
                                        <Save className="w-4 h-4" />
                                        Save Changes
                                    </Button>
                                </div>
                            </form>
                        )}
                    </DialogContent>
                </Dialog>

                {/* Audio Content */}
                <Tabs defaultValue="all" className="space-y-6">
                    <TabsList className="bg-[#121620] border border-white/10">
                        <TabsTrigger value="all" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">
                            All ({audioContent.length})
                        </TabsTrigger>
                        {LANGUAGES.filter(l => audioContent.some(a => a.language === l.code)).map(lang => (
                            <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">
                                {lang.name} ({audioContent.filter(a => a.language === lang.code).length})
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    <TabsContent value="all">
                        <AudioTable 
                            audioContent={audioContent} 
                            playingId={playingId} 
                            onTogglePlay={togglePlay} 
                            onDelete={handleDelete}
                            onEdit={openEditDialog}
                            onUploadImage={handleUploadImage}
                            uploadingImage={uploadingImage}
                            onTranscribe={generateTranscript}
                            transcribingId={transcribing}
                        />
                    </TabsContent>
                    
                    {LANGUAGES.map(lang => (
                        <TabsContent key={lang.code} value={lang.code}>
                            <AudioTable 
                                audioContent={audioContent.filter(a => a.language === lang.code)} 
                                playingId={playingId} 
                                onTogglePlay={togglePlay} 
                                onDelete={handleDelete}
                                onEdit={openEditDialog}
                                onUploadImage={handleUploadImage}
                                uploadingImage={uploadingImage}
                                onTranscribe={generateTranscript}
                                transcribingId={transcribing}
                            />
                        </TabsContent>
                    ))}
                </Tabs>
            </div>
        </DashboardLayout>
    );
}

function AudioTable({ audioContent, playingId, onTogglePlay, onDelete, onEdit, onUploadImage, uploadingImage, onTranscribe, transcribingId }) {
    const API = process.env.REACT_APP_BACKEND_URL;

    if (audioContent.length === 0) {
        return (
            <Card className="bg-[#121620] border-white/10">
                <CardContent className="p-12 text-center">
                    <Music className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-white font-medium mb-2">No audio content yet</h3>
                    <p className="text-muted-foreground text-sm">Upload your first audio file to get started</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            {audioContent.sort((a, b) => a.order - b.order).map((audio) => (
                <Card key={audio.id} className="bg-[#121620] border-white/10 hover:border-white/20 transition-colors">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                            {/* Play Button / Image */}
                            <div className="relative">
                                {audio.image_url ? (
                                    <div className="relative w-20 h-20 rounded-md overflow-hidden">
                                        <img 
                                            src={audio.image_url.startsWith('http') ? audio.image_url : `${API}${audio.image_url}`}
                                            alt={audio.title}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                        <button
                                            onClick={() => audio.file_path && onTogglePlay(audio.id, audio.file_path)}
                                            className={`absolute inset-0 flex items-center justify-center bg-black/50 hover:bg-black/40 transition-colors ${
                                                !audio.file_path ? 'cursor-not-allowed' : ''
                                            }`}
                                            disabled={!audio.file_path}
                                        >
                                            {playingId === audio.id ? (
                                                <Pause className="w-8 h-8 text-white" />
                                            ) : (
                                                <Play className="w-8 h-8 text-white ml-1" />
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => audio.file_path && onTogglePlay(audio.id, audio.file_path)}
                                        className={`w-20 h-20 rounded-md flex items-center justify-center transition-colors ${
                                            audio.file_path 
                                                ? playingId === audio.id 
                                                    ? 'bg-gold animate-pulse' 
                                                    : 'bg-white/10 hover:bg-white/20'
                                                : 'bg-white/5 cursor-not-allowed'
                                        }`}
                                        disabled={!audio.file_path}
                                    >
                                        {playingId === audio.id ? (
                                            <Pause className="w-8 h-8 text-[#0A0D14]" />
                                        ) : (
                                            <Play className="w-8 h-8 text-white ml-1" />
                                        )}
                                    </button>
                                )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <h3 className="text-white font-medium">{audio.order + 1}. {audio.title}</h3>
                                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                                            <span className="flex items-center gap-1">
                                                <Globe className="w-3 h-3" />
                                                {audio.language.toUpperCase()}
                                            </span>
                                            <span className="px-2 py-0.5 rounded-full bg-white/5 text-xs capitalize">
                                                {audio.audio_type}
                                            </span>
                                            {audio.duration && (
                                                <span>{formatDuration(audio.duration)}</span>
                                            )}
                                            {audio.latitude && audio.longitude && (
                                                <span className="flex items-center gap-1 text-gold/70" title={`GPS: ${audio.latitude}, ${audio.longitude}`}>
                                                    <MapPin className="w-3 h-3" />
                                                    GPS
                                                </span>
                                            )}
                                        </div>
                                        {audio.transcript && (
                                            <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{audio.transcript}</p>
                                        )}
                                    </div>
                                    
                                    {/* Actions */}
                                    <div className="flex items-center gap-2 shrink-0">
                                        {/* Upload Image Button */}
                                        <div className="relative">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) onUploadImage(audio.id, file);
                                                }}
                                                className="hidden"
                                                id={`image-${audio.id}`}
                                            />
                                            <label htmlFor={`image-${audio.id}`}>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    className="text-muted-foreground hover:text-gold cursor-pointer"
                                                    asChild
                                                    disabled={uploadingImage === audio.id}
                                                >
                                                    <span>
                                                        {uploadingImage === audio.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Image className="w-4 h-4" />
                                                        )}
                                                    </span>
                                                </Button>
                                            </label>
                                        </div>
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            onClick={() => onEdit(audio)}
                                            className="text-muted-foreground hover:text-white"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </Button>
                                        {audio.file_path && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => onTranscribe(audio.id)}
                                                disabled={transcribingId === audio.id}
                                                className="text-muted-foreground hover:text-gold"
                                                title={audio.transcript ? 'Regenerate Transcript' : 'Generate Transcript'}
                                                data-testid={`transcribe-btn-${audio.id}`}
                                            >
                                                {transcribingId === audio.id ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <FileText className="w-4 h-4" />
                                                )}
                                            </Button>
                                        )}
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            onClick={() => onDelete(audio.id)}
                                            className="text-muted-foreground hover:text-destructive"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}
